// Streak, accuracy, mistake breakdown and correction review — a port of
// `ui/ProgressScreen.kt`, plus the JSON export/import that localStorage makes
// necessary on the web.
import { useRef, useState } from 'react';

import Header from '../components/Header';
import { useStore } from '../store';

export default function ProgressScreen() {
  const s = useStore();
  const p = s.progress;
  const topCount = Math.max(1, p.topMistakes[0]?.count ?? 1);
  const fileRef = useRef<HTMLInputElement>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  return (
    <div className="screen">
      <Header
        title="Your progress"
        subtitle="Stored in this browser only"
        stats={[
          { value: String(p.streakDays), label: 'day streak' },
          { value: String(p.sessions), label: 'conversations' },
          { value: String(p.totalCorrections), label: 'corrections' },
        ]}
      />

      <div className="scroll">
        <div className="card">
          <h2>Accuracy</h2>
          <p className="muted">
            {p.accuracy}% of your {p.totalMessages} sentences needed no correction.
          </p>
          <div className="meter">
            <div style={{ width: `${p.accuracy}%` }} />
          </div>
        </div>

        <div className="card">
          <h2>Speaking practice</h2>
          <p className="muted">
            {p.practiceAttempts === 0
              ? 'No pronunciation attempts yet — try the Pronounce tab.'
              : `${p.practiceAttempts} attempts • best ${p.practiceBest}% • average ${p.practiceAverage}%`}
          </p>
        </div>

        {p.topMistakes.length > 0 && (
          <>
            <div className="section-header">What to work on</div>
            {p.topMistakes.map((mistake) => (
              <div key={mistake.rule}>
                <div className="mistake">
                  <span>{mistake.label}</span>
                  <span className="count">{mistake.count}×</span>
                </div>
                <div className="meter thin">
                  <div style={{ width: `${(mistake.count / topCount) * 100}%` }} />
                </div>
              </div>
            ))}
          </>
        )}

        {p.recentCorrections.length > 0 && (
          <>
            <div className="section-header">Recent corrections</div>
            {p.recentCorrections.slice(0, 20).map((correction, index) => (
              <div className="review" key={`${correction.rule}-${index}`}>
                <div className="swap">
                  “{correction.original}” → “{correction.corrected}”
                </div>
                <div className="why">{correction.explanation}</div>
              </div>
            ))}
          </>
        )}

        {p.totalMessages === 0 && (
          <p className="muted">
            Start a conversation on the Talk tab and your stats will appear here.
          </p>
        )}

        <div className="section-header">Your data</div>
        <p className="muted">
          Clearing this site's data in your browser also clears your progress, so keep a backup if
          it matters to you.
        </p>
        {importError && <div className="notice">{importError}</div>}
        <div className="danger-row">
          <button type="button" className="btn outlined" onClick={s.exportProgress}>
            ⬇ Export JSON
          </button>
          <button type="button" className="btn outlined" onClick={() => fileRef.current?.click()}>
            ⬆ Import JSON
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              setImportError(null);
              try {
                await s.importProgress(file);
              } catch (error) {
                setImportError(
                  error instanceof Error ? error.message : 'That file could not be read.',
                );
              }
            }}
          />
          {confirmingReset ? (
            <>
              <button
                type="button"
                className="btn filled"
                onClick={() => {
                  s.resetProgress();
                  setConfirmingReset(false);
                }}
              >
                Yes, erase everything
              </button>
              <button type="button" className="btn text" onClick={() => setConfirmingReset(false)}>
                Cancel
              </button>
            </>
          ) : (
            <button type="button" className="btn outlined" onClick={() => setConfirmingReset(true)}>
              Reset my progress
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
